/**
 * UI-6b Task 8 — AgentUtilization widget renderer.
 *
 * Bar chart of utilization % per agent over the last 24h, sourced from
 * /reports/agent-performance?from=<24h-ago>&to=<now>. The endpoint supports
 * a single `agentId` filter; for "multiple selected agents" Phase 1 we fetch
 * all agents in one call and filter client-side so the dashboard widget
 * keeps a single round-trip.
 *
 * Config: { agentIds?: string[] }. Empty array → show all agents the
 * endpoint returns (top by callsHandled, the backend's ORDER BY).
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import { api } from '../../../../api/client.js'

export interface AgentUtilizationConfig {
  agentIds?: string[]
}

interface AgentPerformanceRow {
  agentId: string
  agentName: string | null
  callsHandled: number | string
  utilizationPct: number | string | null
}

interface AgentPerformanceResponse {
  rows: AgentPerformanceRow[]
}

function buildRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getTime() - 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

function toNumber(v: number | string | null | undefined): number {
  if (v == null) return 0
  return typeof v === 'number' ? v : Number.parseFloat(v) || 0
}

export function AgentUtilizationRenderer({
  config,
}: {
  config: AgentUtilizationConfig
}): JSX.Element {
  const [rows, setRows] = useState<AgentPerformanceRow[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const agentIdsKey = (config.agentIds ?? []).join(',')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const { from, to } = buildRange()
    const query = new URLSearchParams({ from, to })

    api
      .get<AgentPerformanceResponse>(`/reports/agent-performance?${query.toString()}`)
      .then((resp) => {
        if (cancelled) return
        const filter = (config.agentIds ?? []).filter((s) => s.length > 0)
        const filtered =
          filter.length > 0
            ? resp.rows.filter((r) => filter.includes(r.agentId))
            : resp.rows
        setRows(filtered)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load agent performance')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return (): void => {
      cancelled = true
    }
  }, [agentIdsKey])

  const chartData = useMemo(() => {
    if (!rows) return null
    return {
      labels: rows.map((r) => r.agentName ?? r.agentId.slice(0, 8)),
      datasets: [
        {
          label: 'Utilization %',
          backgroundColor: '#10b981',
          data: rows.map((r) => toNumber(r.utilizationPct)),
        },
      ],
    }
  }, [rows])

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      scales: {
        y: { beginAtZero: true, max: 100, ticks: { precision: 0 } },
      },
    }),
    [],
  )

  if (loading) {
    return (
      <div data-testid="agent-utilization-loading" className="text-sm text-gray-500">
        Loading agent utilization…
      </div>
    )
  }
  if (error) {
    return (
      <div data-testid="agent-utilization-error" className="text-sm text-red-600">
        {error}
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <div data-testid="agent-utilization-empty" className="text-sm text-gray-500">
        No agent activity in the last 24 hours.
      </div>
    )
  }

  if (!chartData) return <></>

  return (
    <div data-testid="agent-utilization-widget" className="h-full w-full">
      <Chart type="bar" data={chartData} options={chartOptions} />
    </div>
  )
}
